import React, { useMemo, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { Plus, Trash2, Receipt, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';

export function ExpenseTracker({ submission, onUpdate }) {
  const { saveExpenses } = useData();
  const { lang } = useT();
  const expenses = submission?.expenses || [];
  const [draft, setDraft] = useState({ description: '', amount: '', category: 'material', date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const estimatedTotal = (submission?.summary?.materialsTotal || 0) + (submission?.summary?.labourTotal || 0);

  const totals = useMemo(() => {
    const byCat = { material: 0, labour: 0, other: 0 };
    expenses.forEach((e) => {
      const amt = Number(e.amount) || 0;
      byCat[e.category || 'other'] = (byCat[e.category || 'other'] || 0) + amt;
    });
    const totalSpent = byCat.material + byCat.labour + byCat.other;
    const grandTotal = submission?.summary?.grandTotal || 0;
    const realProfit = grandTotal - totalSpent;
    return { byCat, totalSpent, realProfit, grandTotal };
  }, [expenses, submission]);

  const add = async () => {
    if (!draft.description.trim() || !draft.amount) {
      toast.error(lang === 'fr' ? 'Description et montant requis' : 'Description and amount required');
      return;
    }
    const item = {
      id: crypto.randomUUID(),
      description: draft.description.trim(),
      amount: Number(draft.amount),
      category: draft.category,
      date: draft.date,
      created_at: new Date().toISOString(),
    };
    setSaving(true);
    try {
      await saveExpenses(submission.id, [...expenses, item]);
      setDraft({ description: '', amount: '', category: draft.category, date: new Date().toISOString().slice(0, 10) });
      if (onUpdate) onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await saveExpenses(submission.id, expenses.filter((e) => e.id !== id));
    if (onUpdate) onUpdate();
  };

  const profitColor = totals.realProfit >= 0 ? 'text-emerald-700' : 'text-red-700';

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
          {lang === 'fr' ? 'Dépenses réelles' : 'Real expenses'}
        </div>
        <Receipt size={16} className="text-gray-400" />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label={lang === 'fr' ? 'Estimé' : 'Estimated'} value={money(estimatedTotal)} color="text-[#7A1F2B]" />
        <Stat label={lang === 'fr' ? 'Dépensé' : 'Spent'} value={money(totals.totalSpent)} color="text-amber-700" />
        <Stat
          label={lang === 'fr' ? 'Profit réel' : 'Real profit'}
          value={money(totals.realProfit)}
          color={profitColor}
          icon={totals.realProfit >= 0 ? TrendingUp : TrendingDown}
        />
      </div>

      <div className="space-y-2 mb-4">
        <div className="grid grid-cols-3 gap-2">
          <select
            data-testid="expense-category"
            value={draft.category}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            className="field !min-h-[44px]"
          >
            {(() => {
              const opts = lang === 'fr'
                ? [{ v: 'material', l: 'Matériaux' }, { v: 'labour', l: 'Main-d\u2019\u0153uvre' }, { v: 'other', l: 'Autre' }]
                : [{ v: 'material', l: 'Materials' }, { v: 'labour', l: 'Labour' }, { v: 'other', l: 'Other' }];
              return opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>);
            })()}
          </select>
          <input
            data-testid="expense-date"
            type="date"
            value={draft.date}
            onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            className="field !min-h-[44px]"
          />
          <input
            data-testid="expense-amount"
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="0.00 $"
            value={draft.amount}
            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            className="field !min-h-[44px]"
          />
        </div>
        <input
          data-testid="expense-description"
          placeholder={lang === 'fr' ? 'Description (ex: Achat plywood Rona)' : 'Description (e.g. Plywood at Rona)'}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          className="field"
        />
        <button
          data-testid="expense-add"
          onClick={add}
          disabled={saving}
          className="btn-outline w-full gap-2 border-dashed disabled:opacity-50"
        >
          <Plus size={16} /> {lang === 'fr' ? 'Ajouter une dépense' : 'Add expense'}
        </button>
      </div>

      {expenses.length > 0 && (
        <div className="space-y-2">
          {expenses
            .slice()
            .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
            .map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#FAF7F2] border border-[#E5DCC9]">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{e.description}</div>
                  <div className="text-[10px] text-gray-500">
                    {new Date(e.date).toLocaleDateString()} · {e.category}
                  </div>
                </div>
                <div className="font-heading font-black">{money(e.amount)}</div>
                <button
                  data-testid={`expense-remove-${e.id}`}
                  onClick={() => remove(e.id)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, icon: Icon }) {
  return (
    <div className="rounded-xl border-2 border-[#E5DCC9] p-3 text-center">
      <div className="flex items-center justify-center gap-1 mb-1">
        {Icon && <Icon size={11} className={color} />}
        <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500">{label}</span>
      </div>
      <div className={`font-heading font-black text-base ${color}`}>{value}</div>
    </div>
  );
}
